import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import '../shared/sso-workspace/sso-workspace.scss'
import '../shared/sso-consent/sso-consent.scss'
import { useQueryParam } from '../shared/hooks/location'
import { useLoad } from '../shared/hooks/load'
import { useApiFormProviderConsentReview } from '../api-new/state/auth'
import { formatDate } from '../shared/sso-consent/consent-model'
import { AuthHead } from '../shared/sso-workspace/auth-card'
import { ReturnScene } from '../shared/sso-workspace/return-card'

// The two PUBLIC pages a customer's IT administrator sees (spec 2026-09-26).
// That person is almost never an EducationXR user, so neither page needs a
// sign-in, and both explain themselves in plain sentences:
//
//   /open/sso-consent/:token   the review page the emailed or copied link
//                              opens: who asked, for which organization and
//                              Microsoft directory, what approving does, and
//                              "Continue to Microsoft".
//   /open/sso-consent?result=  where Microsoft's approval lands: approved, or
//                              why the link could not be used. The only other
//                              thing in the URL is the request's review token
//                              (r=), which the page looks the organization's
//                              name up by.

const PublicCard = ({ children }) =>
  <div className="sw-workspace sc-public">
    <main className="sc-card">
      <img className="sc-logo" src="/EDXR-Logo.png" alt="EducationXR" />
      {children}
    </main>
  </div>

// A finished approval is the outcome card's moment (return-card.js, owner
// 2026-10-24): their organization, the mark, EducationXR, with the short
// animation. Every other outcome keeps the small mark beside its title.
const Outcome = ({ tone, mark, title, children }) =>
  tone === 'ok'
    ? <div className="sa-return">
        <ReturnScene variant="consent" />
        <AuthHead title={title} center />
        <div className="sa-return-body">{children}</div>
      </div>
    : <>
        <div className="sc-outcome">
          <span className={`sc-outcome-mark ${tone || ''}`.trim()} aria-hidden="true">{mark}</span>
          <h1>{title}</h1>
        </div>
        {children}
      </>

// Every result the callback (and the review page, for a link that can no
// longer be used) can report, in words. `who` is the organization's name, or
// a stand-in when the link didn't say.
const OUTCOMES = {
  approved: who => ({
    tone: 'ok', mark: '✓', title: "You're done",
    body: [`EducationXR single sign-on is approved for ${who.org}.`, `${who.asker} has been notified and will finish setup. You can close this window.`]
  }),
  cancelled: who => ({
    tone: 'warn', mark: '!', title: 'This approval link was cancelled',
    body: ['The request was cancelled, so nothing was changed.', `If you still want to approve it, ask ${who.ask} for a new link.`]
  }),
  expired: who => ({
    tone: 'warn', mark: '!', title: 'This approval link expired',
    body: ['Approval links work for 7 days. Nothing was changed.', `Ask ${who.ask} for a new link.`]
  }),
  superseded: who => ({
    tone: 'warn', mark: '!', title: 'This approval link was replaced',
    body: ['A newer request was sent for the same Microsoft organization, so this link no longer works. Nothing was changed.', `Use the link in the newest email, or ask ${who.ask} for a new one.`]
  }),
  mismatch: who => ({
    tone: 'bad', mark: '!', title: 'That was a different Microsoft organization',
    body: ['You approved in a Microsoft organization other than the one this request is for, so nothing was changed.', `Open the link again and sign in with an administrator account for the right organization, or ask ${who.ask} for a new link.`]
  }),
  denied: who => ({
    tone: 'warn', mark: '!', title: 'The request was declined',
    body: ['Microsoft reports that the request was not approved, so nothing was changed.', `If that was a mistake, open the link again. Otherwise let ${who.ask} know.`]
  }),
  invalid: who => ({
    tone: 'bad', mark: '!', title: "This link isn't valid",
    body: ['It may be incomplete, or from an older email. Nothing was changed.', `Ask ${who.ask} for a new link.`]
  }),
  error: who => ({
    tone: 'bad', mark: '!', title: "We couldn't finish on our side",
    body: ['Your approval reached EducationXR, but setting up the connection failed.', `Nothing more is needed from you now. Please let ${who.ask} know so they can contact EducationXR support.`]
  })
}

const whoFor = org =>
  org
    ? { org, asker: `The person at ${org} who asked`, ask: `the person at ${org} who sent it` }
    : { org: 'your organization', asker: 'The person who asked', ask: 'the person who sent it' }

export const ConsentOutcome = ({ result, org }) => {
  const outcome = (OUTCOMES[result] || OUTCOMES.invalid)(whoFor(org))
  return (
    <Outcome tone={outcome.tone} mark={outcome.mark} title={outcome.title}>
      {outcome.body.map(line => <p key={line}>{line}</p>)}
    </Outcome>
  )
}

// The result page is told which request it was by the request's opaque
// review token (?r=), never what to say: the organization's name comes from
// the server (review M1). Anything else in the address is ignored, so the
// page can't be made to show arbitrary text under EducationXR's name.
export const OpenSsoConsentResult = () => {
  const result = useQueryParam('result')
  const token = useQueryParam('r')
  const review = useApiFormProviderConsentReview()
  const [org, setOrg] = useState(undefined)

  useLoad(async () => {
    if (!token) {
      setOrg(null)
      return
    }
    try {
      const data = await review.get(token)
      setOrg((data && data.organizationName) || null)
    } catch (err) {
      setOrg(null)
    }
  }, [token])

  if (org === undefined) {
    return (
      <PublicCard>
        <p className="sw-hint" role="status">Loading…</p>
      </PublicCard>
    )
  }

  return (
    <PublicCard>
      <ConsentOutcome result={result} org={org} />
    </PublicCard>
  )
}

export const OpenSsoConsentReview = () => {
  const { token } = useParams()
  const review = useApiFormProviderConsentReview()

  const [state, setState] = useState({ loading: true })
  const [explain, setExplain] = useState(false)

  useLoad(async () => {
    try {
      const data = await review.get(token)
      setState({ data })
    } catch (err) {
      setState({ failed: true, notFound: !!err && err.status === 404 })
    }
  }, [token])

  if (state.loading) {
    return (
      <PublicCard>
        <p className="sw-hint" role="status">Loading the request…</p>
      </PublicCard>
    )
  }

  if (state.failed) {
    return (
      <PublicCard>
        {state.notFound
          ? <ConsentOutcome result="invalid" />
          : <Outcome tone="bad" mark="!" title="This page couldn't load">
            <p>Check your connection and reload the page. Nothing has been changed.</p>
          </Outcome>}
      </PublicCard>
    )
  }

  const data = state.data

  if (data.status !== 'pending') {
    return (
      <PublicCard>
        {data.status === 'approved'
          ? <Outcome tone="ok" mark="✓" title="Already approved">
            <p>Single sign-on to EducationXR is already approved for {data.organizationName}. Nothing more is needed. You can close this window.</p>
          </Outcome>
          : <ConsentOutcome result={data.status} org={data.organizationName} />}
      </PublicCard>
    )
  }

  const directory = data.domain || `${data.tenantShort}…`
  const who = data.onBehalf
    ? `${data.requesterName} from EducationXR asked, on behalf of ${data.organizationName}, to connect EducationXR to your Microsoft organization.`
    : `${data.requesterName} at ${data.organizationName} asked to connect EducationXR to your Microsoft organization.`

  return (
    <PublicCard>
      <h1>Approve single sign-on to EducationXR</h1>
      <p>{who}</p>

      <dl className="sc-facts">
        <div>
          <dt>Microsoft organization</dt>
          <dd>
            <span className="sw-mono">{directory}</span>
            <span className="sw-hint d-block">Only an administrator of this Microsoft organization can approve.</span>
          </dd>
        </div>
        <div>
          <dt>What happens</dt>
          <dd>{data.domain ? `People at ${data.domain}` : 'People in this Microsoft organization'} will be able to sign in to {data.organizationName} in EducationXR with their Microsoft account.</dd>
        </div>
        <div>
          <dt>Permissions</dt>
          <dd>{data.permissions}</dd>
        </div>
        <div>
          <dt>Link expires</dt>
          <dd>{formatDate(data.expiresAt)}</dd>
        </div>
      </dl>

      <div className="sc-actions">
        <a className="btn sw-btn sw-btn-primary" href={data.consentURL}>Continue to Microsoft</a>
        <button type="button" className="btn sw-btn" aria-expanded={explain} aria-controls="sc-not-right" onClick={() => setExplain(v => !v)}>This isn't right</button>
      </div>

      {explain &&
        <div className="sc-note" id="sc-not-right">
          <p className="mb-2">If you don't recognise this request, don't continue. Nothing changes unless an administrator approves it in Microsoft, and this link stops working on {formatDate(data.expiresAt)}.</p>
          <p>
            {data.supportEmail
              ? <>Questions? Contact EducationXR at <a href={`mailto:${data.supportEmail}`}>{data.supportEmail}</a>.</>
              : <>Questions? Reply to the email this link came in, or contact {data.organizationName} directly.</>}
          </p>
        </div>}
    </PublicCard>
  )
}
